import React, { useCallback, useMemo } from 'react'
import classNames from 'classnames'
import { When } from 'react-if'
import { LinkButton } from '@/atoms/Button'
import { ExternalLink } from '@/atoms/ExternalLink'
import { QuestionMarkFilled } from '@/atoms/Icons/QuestionMarkFilled'
import { InternalLink } from '@/atoms/InternalLink'
import { AngelLogo } from '@/atoms/Logos/AngelLogo'
import { LabelSM } from '@/atoms/Text'
import { paths } from '@/constants/paths'
import { useExperimentContext } from '@/experimentation'
import { LanguageMenu } from '@/molecules/LanguageSelector'
import { NotificationsMenu } from '@/molecules/Notifications'
import { RegionSelector } from '@/molecules/RegionSelector'
import { OmniboxSearchInput } from '@/organisms/Search'
import { ANGXStockTicker } from '@/organisms/SiteNav/ANGXStockTicker'
import { NavMenu } from '@/organisms/SiteNav/NavMenu'
import { useSiteNavContext } from '@/organisms/SiteNav/SiteNavContext'
import { UserMenu } from '@/organisms/SiteNav/UserMenu'
import { useGuildMember } from '@/services/GuildUserService'
import { useUser } from '@/services/UserService'
import { useSafeTrack } from '@/utils/analytics'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { Translate, useTranslate } from '@/utils/translate/translate-client'

export const DesktopSiteNav: React.FC = () => {
  const { t } = useTranslate('common')
  const { isLoggedIn, hasAuthCookie, loading: loadingUser } = useUser()
  const { isGuildMember, loading: loadingGuild } = useGuildMember()
  const track = useSafeTrack()
  const router = useSafeRouter()
  const { getFeatureValue, ready } = useExperimentContext()
  const { pathname, asPath } = router || {}
  const normalizedPath = useMemo(() => {
    // Prefer asPath (captures resolved dynamic locale segment); fallback to pathname
    const raw = asPath || pathname || ''
    // Strip query then hash fragments defensively
    const firstSplit = (raw || '').split('?')[0]
    const secondSplit = (firstSplit || '').split('#')[0]
    const pathOnly = secondSplit || '/'
    // Remove a leading locale folder (e.g. /en, /es, /en-US)
    const withoutLocale = pathOnly.replace(/^\/(?:[a-zA-Z]{2}(?:-[a-zA-Z]{2})?)(?=\/|$)/, '')
    const normalized = withoutLocale === '' ? '/' : withoutLocale
    // Do not strip the only slash on root; only trim trailing slash for longer paths
    return normalized === '/' ? '/' : normalized.replace(/\/$/, '')
  }, [asPath, pathname])
  const showStockTicker = useMemo(() => {
    const isRoot = normalizedPath === '/' || normalizedPath === ''
    const isDefaultPage = isRoot || normalizedPath === '/home' || normalizedPath === '/watch'

    // Check if user is on /guild/join with origin=home query param
    const isGuildJoinFromHome = normalizedPath === '/guild/join' && router?.query?.origin === 'home'

    if (isGuildJoinFromHome && ready) {
      return getFeatureValue('angx-ticker-on-home', false)
    }

    return isDefaultPage
  }, [normalizedPath, router?.query?.origin, ready, getFeatureValue])

  const isInNonGiftGuildPath = useMemo(() => {
    const disallowedWildcardPaths = ['/guild/gift']
    return !disallowedWildcardPaths.some((path) => normalizedPath.startsWith(path))
  }, [normalizedPath])

  const isInShowJoinGuildPath = useMemo(() => {
    const allowedPaths = ['/', '/home', '/watch', '/account', '/blog', '/guild/voting', '/guild/impact']

    if (allowedPaths.includes(normalizedPath)) {
      return true
    }

    const wildcardPaths = ['/watch/', '/account/', '/category/']
    return wildcardPaths.some((path) => normalizedPath.startsWith(path))
  }, [normalizedPath])

  const {
    handleOpenWatchMenu,
    handleOpenComingSoonMenu,
    handleOpenGuildMenu,
    handleOpenNavMenu,
    handleCloseNavMenu,
    openedNavMenu,
    handleOpenRegionModal,
    handleCloseLanguageModal,
    hideNavigation,
    isWatchMenuOpen,
    isComingSoonMenuOpen,
    isGuildMenuOpen,
    notificationsCount,
    regions,
    fetchOnlyNew: shouldFetchOnlyNewNotifications,
    shouldUseDarkMode,
    showLanguageAndHelpLinks,
    showNotifications: shouldShowNotifications,
    variant,
    variantStickyClassNames,
  } = useSiteNavContext()

  const handleNavShopClick = useCallback(() => {
    track('Nav Shop Clicked')
  }, [track])

  const handleUpgradeClick = useCallback(() => {
    track('Nav Upgrade CTA Clicked')
  }, [track])

  const handleGiftGuildClick = useCallback(() => {
    track('Gift Guild CTA Clicked', { location: 'desktop_sitenav', isGuildMember })
    handleCloseNavMenu()
  }, [track, handleCloseNavMenu, isGuildMember])

  const isShowGiftGuildCta = isInNonGiftGuildPath && !loadingGuild && isGuildMember

  const showJoinGuildButton = isInShowJoinGuildPath && !loadingUser && !loadingGuild && (!isLoggedIn || !isGuildMember)

  return (
    <div
      className={classNames(
        'top-0 inset-x-0 z-50 hidden xl:flex h-16 w-full flex-row items-center justify-between px-6 py-4',
        variant === 'sticky' ? variantStickyClassNames : 'absolute',
      )}
    >
      <div className="flex flex-row items-center justify-start space-x-2 xl:space-x-6">
        {/* Angel Logo */}
        <LogoLink shouldUseDarkMode={shouldUseDarkMode} isLoggedIn={isLoggedIn} />

        {/* Site Navigation v2*/}
        <When condition={!hideNavigation}>
          <nav aria-labelledby="desktop-nav-title">
            <h2 id="desktop-nav-title" className="sr-only">
              <Translate t={t} i18nKey="desktopMenu">
                Desktop Menu
              </Translate>
            </h2>
            <ul className="flex flex-row space-x-2 2xl:space-x-6">
              <li>
                <NavMenu
                  label={t('watch', 'Watch')}
                  href={paths.watch.index}
                  onOpen={handleOpenWatchMenu}
                  isOpen={isWatchMenuOpen}
                  onClose={handleCloseNavMenu}
                  id="watch-menu"
                >
                  <NavMenu.Item
                    Element={InternalLink}
                    label={t('comedy', 'Comedy')}
                    href={paths.category.comedyTvShows}
                    id="comedy-category"
                    onClick={handleCloseNavMenu}
                  />
                  <NavMenu.Item
                    Element={InternalLink}
                    label={t('drama', 'Drama')}
                    href={paths.category.drama}
                    id="drama-category"
                    onClick={handleCloseNavMenu}
                  />
                  <NavMenu.Item
                    Element={InternalLink}
                    label={t('documentary', 'Documentary')}
                    href={paths.category.documentaries}
                    id="documentary-category"
                    onClick={handleCloseNavMenu}
                  />
                  <NavMenu.Item
                    Element={InternalLink}
                    label={t('family', 'Family')}
                    href={paths.category.family}
                    id="family-category"
                    onClick={handleCloseNavMenu}
                  />
                  <NavMenu.Item
                    Element={InternalLink}
                    label={t('free', 'Free')}
                    href={paths.category.free}
                    id="free-category"
                    onClick={handleCloseNavMenu}
                  />
                  <NavMenu.Item
                    Element={InternalLink}
                    label={t('sports', 'Sports')}
                    href={paths.category.sports}
                    id="sports-category"
                    onClick={handleCloseNavMenu}
                  />
                  <NavMenu.Item
                    Element={InternalLink}
                    label={t('faithBased', 'Faith Based')}
                    href={paths.category.faithBased}
                    id="faith-based-category"
                    onClick={handleCloseNavMenu}
                  />
                </NavMenu>
              </li>
              {hasAuthCookie && (loadingUser || isLoggedIn) && (
                <li>
                  <NavMenu
                    label={t('menuLinkNewUpcoming', 'New & Upcoming')}
                    href={paths.category.newReleases}
                    onOpen={handleOpenComingSoonMenu}
                    isOpen={isComingSoonMenuOpen}
                    onClose={handleCloseNavMenu}
                    id="new-upcoming-menu"
                  >
                    <NavMenu.Item
                      Element={InternalLink}
                      label={t('menuLinkNewReleases', 'New Releases')}
                      href={paths.category.newReleases}
                      id="new-releases"
                      onClick={handleCloseNavMenu}
                    />
                    <NavMenu.Item
                      Element={InternalLink}
                      label={t('comingSoon', 'Coming Soon')}
                      href={paths.comingSoon.index}
                      id="coming-soon"
                      onClick={handleCloseNavMenu}
                    />
                  </NavMenu>
                </li>
              )}
              <li>
                <NavMenu.Item
                  Element={InternalLink}
                  label={t('inTheaters', 'In Theaters')}
                  href={paths.tickets.index}
                  id="in-theaters"
                />
              </li>
              <li>
                <NavMenu
                  label={t('guild', 'Guild')}
                  href={
                    isGuildMember
                      ? `${paths.guild.index}?utm_medium=web&utm_campaign=guild-memberships&utm_source=sitenav`
                      : `${paths.guild.join}?utm_medium=web&utm_campaign=guild-memberships&utm_source=sitenav`
                  }
                  onOpen={handleOpenGuildMenu}
                  isOpen={isGuildMenuOpen}
                  onClose={handleCloseNavMenu}
                  id="guild-menu"
                >
                  <NavMenu.Item
                    Element={InternalLink}
                    label={isGuildMember ? t('myBenefits', 'My Benefits') : t('learnMore', 'Learn More')}
                    href={
                      isGuildMember
                        ? `${paths.guild.index}?utm_medium=web&utm_campaign=guild-memberships&utm_source=sitenav`
                        : `${paths.guild.join}?utm_medium=web&utm_campaign=guild-memberships&utm_source=sitenav`
                    }
                    id="guild-dashboard-join"
                    linkContext="menu-link"
                    role="menuitem"
                    onClick={handleCloseNavMenu}
                  />
                  <NavMenu.Item
                    Element={InternalLink}
                    label={t('filmmakers', 'Filmmakers')}
                    href={paths.filmmakers.index}
                    id="filmmakers"
                    linkContext="menu-link"
                    role="menuitem"
                    onClick={handleCloseNavMenu}
                  />
                  <NavMenu.Item
                    Element={InternalLink}
                    label={t('angelImpact', 'Angel Impact')}
                    href={paths.guild.impact}
                    id="guild-impact"
                    linkContext="menu-link"
                    role="menuitem"
                    onClick={handleCloseNavMenu}
                  />
                  <NavMenu.Item
                    Element={InternalLink}
                    label={t('guildVoting', 'Guild Voting')}
                    href={paths.guild.voting}
                    id="submit-your-project"
                    linkContext="menu-link"
                    role="menuitem"
                    onClick={handleCloseNavMenu}
                  />
                  <NavMenu.Item
                    Element={InternalLink}
                    label={t('giftTheGuild', 'Gift the Guild')}
                    href={isGuildMember ? paths.guild.gift.index : paths.external.giftGuild}
                    id="gift-the-guild"
                    linkContext="menu-link"
                    role="menuitem"
                    onClick={handleGiftGuildClick}
                  />
                </NavMenu>
              </li>
              {hasAuthCookie && (loadingUser || isLoggedIn) && (
                <>
                  <li>
                    <NavMenu.Item
                      Element={ExternalLink}
                      label={t('menuLinkShop', 'Shop')}
                      href={paths.external.shop.index}
                      id="shop"
                      className="hidden [@media(min-width:1400px)]:inline-block"
                      onClick={handleNavShopClick}
                    />
                  </li>
                  <li>
                    <NavMenu.Item
                      Element={InternalLink}
                      label={t('blog', 'Blog')}
                      href={paths.blog.index}
                      id="blog"
                      className="hidden [@media(min-width:1400px)]:inline-block"
                    />
                  </li>
                </>
              )}
            </ul>
          </nav>
        </When>
      </div>

      {/* User Nav */}
      <div className="flex flex-row items-center space-x-2 xl:space-x-6">
        {showStockTicker && <ANGXStockTicker className="hidden xl:flex" />}

        {isShowGiftGuildCta && (
          <LinkButton
            href={paths.guild.gift.index}
            variant="white"
            onClick={handleGiftGuildClick}
            className="max-h-8 rounded-lg !border-[#FDA21E] !bg-[#FDA21E] px-4 py-2"
          >
            <LabelSM weight="semibold" color="core-gray-950" className="text-xs">
              {t('giftTheGuild', 'Gift the Guild')}
            </LabelSM>
          </LinkButton>
        )}

        {showJoinGuildButton && (
          <LinkButton
            href={paths.guild.pricing}
            variant="white"
            onClick={handleUpgradeClick}
            className="max-h-8 rounded-lg !border-[#FDA21E] !bg-[#FDA21E] px-4 py-2"
          >
            <LabelSM weight="semibold" color="core-gray-950" className="text-xs">
              {t('upgrade', 'Upgrade')}
            </LabelSM>
          </LinkButton>
        )}

        <OmniboxSearchInput />
        {regions && regions?.length > 0 && (
          <RegionSelector
            onRegionModalOpen={handleOpenRegionModal}
            onLanguageModalClose={handleCloseLanguageModal}
            onClose={handleCloseNavMenu}
            onOpen={() => handleOpenNavMenu('region')}
            isOpen={openedNavMenu === 'region'}
            isDarkMode={shouldUseDarkMode}
            regions={regions}
          />
        )}
        {!showLanguageAndHelpLinks && (
          <>
            <LanguageMenu
              onOpen={() => handleOpenNavMenu('language')}
              isOpen={openedNavMenu === 'language'}
              onClose={handleCloseNavMenu}
              isDarkMode={shouldUseDarkMode}
            />
            <ExternalLink aria-label={t('help', 'Help')} href={paths.external.zendesk.support}>
              <QuestionMarkFilled
                color1={shouldUseDarkMode ? 'white' : 'none'}
                color2={shouldUseDarkMode ? 'core-gray-950' : 'black'}
              />
            </ExternalLink>
          </>
        )}
        {shouldShowNotifications && (
          <NotificationsMenu
            notificationsCount={notificationsCount}
            fetchOnlyNew={shouldFetchOnlyNewNotifications}
            isOpen={openedNavMenu === 'notification'}
            onClose={handleCloseNavMenu}
            onOpen={() => handleOpenNavMenu('notification')}
            isDarkMode={shouldUseDarkMode}
          />
        )}

        <UserMenu loginButtonVariant={shouldUseDarkMode ? 'white' : 'black'} />
      </div>
    </div>
  )
}

function LogoLink(props: { shouldUseDarkMode: boolean; isLoggedIn: boolean }) {
  return (
    <InternalLink href={props.isLoggedIn ? paths.watch.index : paths.index}>
      <When condition={props.shouldUseDarkMode}>
        <AngelLogo className="min-w-[50px]" color="white" />
      </When>
      <When condition={!props.shouldUseDarkMode}>
        <AngelLogo className="min-w-[50px]" color="black" />
      </When>
    </InternalLink>
  )
}
